(ns rothko.editor.behaviours.backspace
  (:require [com.rpl.specter :as sp]
            [rothko.editor.actions :as actions]
            [rothko.editor.document.navigators :as n]
            [rothko.editor.edit.paths :as edit-paths]
            [rothko.editor.mark.transform :as mark-transform]
            [rothko.editor.matcher :as m]
            [rothko.editor.space.transform :as space-transform]
            [rothko.editor.tag.transform :as tag-transform]
            [rothko.editor.util :as util :include-macros true]
            [rothko.editor.word.transform :as word-transform]))

(defn transform-caret
  [{:keys [move mark-spaces-as-edited? space-edits?] :as opts}]
  (let [expand-selection (n/expand-selection {:shift-start-offset -1})
        jump-over-char (case move
                         :left (n/expand-selection
                                {:shift-start-offset -1
                                 :shift-end-offset -1})
                         :right [sp/DISPENSE
                                 actions/no-op])
        removable-space (space-transform/removable-space space-edits?)]
    (util/cond-path

     ;; Multiple adjacent spaces or a space at the start/end of a script - mark as edited / remove.
     (n/and-paths
      (n/context? [m/?any [{:type :space :edited? m/absent}] m/?any])
      (n/or-paths
       n/end-of-script?
       (n/context? [n/script-start-matcher m/?any m/?any])
       (n/char-context? [#"\s\s" n/caret-matcher m/?any])
       (n/char-context? [#"\s\s?" n/caret-matcher #"\s\s?"])))
     (sp/multi-path
      expand-selection
      (if mark-spaces-as-edited?
        (space-transform/space-split)
        n/remove-selection))

     ;; A newline (as a space token) - mark it as edited.
     (n/and-paths
      (n/context?
       [m/?any [{:type :space :edited? m/absent}] m/?any])
      (n/char-context? ["\n" n/caret-matcher n/end-of-word]))
     (sp/multi-path
      (n/expand-selection {:new-start-offset 0})
      (edit-paths/create-from-selection "" opts)
      jump-over-char)

     ;; A space-edit followed by a space (the current token) - mark it as edited.
     ;; TODO: Probably to be replaced with something more general. Added just to satisfy
     ;; a pre-existing test (delete/caret-after-space-edit-creates-the-correct-edit).
     ;; If we keep this rule then dedupe 'then' part with the newline rule.
     (n/and-paths
      (n/context?
       [(m/one-of [{:type :space-edit}] ;; TODO rothko#196 part seq matcher
                      [m/?any {:type :space-edit}])
        [{:type :space :edited? m/absent}]
        m/?any])
      n/end-of-token?)
     (sp/multi-path
      (n/expand-selection {:new-start-offset 0})
      (edit-paths/create-from-selection "" opts))

     (n/and-paths
      (n/context?
       [(m/every (m/one-of {:type :tag} {:type :word} {:type :edit} {:type :space} {:type :space-edit}))
        (m/every (m/one-of {:type :edit} {:type removable-space}))
        (m/every (m/one-of {:type :tag} {:type :word} {:type :edit} {:type :space} {:type :space-edit}))])
      (n/or-paths
       (n/context?
        [(m/every (m/one-of {:type :edit} {:type :space} {:type :space-edit}))
         ;; We already know we're in an edit or space token
         m/?any
         (m/every (m/one-of {:type :edit} {:type :space} {:type :space-edit}))])
       ;; Because of the above cond-path clause we know that if the
       ;; below matches it must be a caret after a single space.
       ;; This means we're trying to merge the surrounding tokens
       ;; and we only want to do that if the selection and
       ;; surrounding context are all edits which is what the above
       ;; context tests for.
       (n/context? [m/?any (m/not-matcher [{:type :space}]) m/?any])))
     (sp/multi-path
      expand-selection
      n/remove-selection
      (n/merge-similar))

     (n/context?
      [m/?any
       [{:type :word
         :edited? m/absent}]
       m/?any])
     (sp/multi-path
      expand-selection
      (edit-paths/create-from-selection "" opts)
      mark-transform/remove-system?-flags)

     (n/and-paths
      (n/context?
       [m/?any
        (m/every (m/one-of
                  {:type :tag :edited? true}
                  {:type :word :edited? true}
                  {:type :space :edited? true}))
        m/?any])
      n/edited?)
     (sp/multi-path
      word-transform/remove-edited?-marking
      (n/expand-selection
       (case move
         :left {:new-start-offset 0
                :new-end-offset 0}
         :right {:new-start-offset :last
                 :new-end-offset :last})))

     (n/context? [m/?any [{:type :tag}] m/?any])
     (sp/multi-path
      tag-transform/remove-at-selection
      (n/merge-similar))

     ;; Remove a space to join two words.
     (n/and-paths
      (n/context?
       ;; TODO this or-matcher should maybe be something like
       ;; (m/or-matcher m/?any m/absent)
       ;; TODO rothko#196 part seq matcher
       [(m/one-of
         [{:type :word
           :edited? m/absent}]
         [m/?any
          {:type :word
           :edited? m/absent}])
        [{:type :space}]
        (m/one-of
         [{:type :word
           :edited? m/absent}]
         [{:type :word
           :edited? m/absent}
          m/?any])])
      (n/char-context? [" " n/caret-matcher n/end-of-word]))
     (sp/multi-path
      (n/expand-selection
       {:shift-start-index -1
        :new-start-offset :last
        :shift-end-index 1
        :new-end-offset 0})
      (edit-paths/create-from-selection "" opts))

     ;; Mark a space as edited / Jump over a space.
     (n/and-paths
      (n/context?
       [(m/every (m/one-of {:type :word} {:type :edit} {:type :space} {:type :space-edit}))
        [{:type :space}]
        (m/every (m/one-of {:type :word} {:type :edit} {:type :space} {:type :space-edit}))])
      (n/char-context? [#"  ?" n/caret-matcher n/end-of-word]))
     (if mark-spaces-as-edited?
       (sp/multi-path
        expand-selection
        (space-transform/space-split))
       jump-over-char))))

(defn transform-selection
  [{:keys [remove-dangling-spaces? mark-spaces-as-edited? space-edits?] :as opts}]
  (let [edit-or-space-edit (m/one-of :edit :space-edit)
        removable-space (space-transform/removable-space space-edits?)]
    [(when remove-dangling-spaces?
       space-transform/remove-dangling-spaces)
     tag-transform/include-whole-tag
     (util/cond-path

      (n/and-paths
       n/entire-word-selected?
       (n/context?
        [(m/not-matcher
          (m/one-of
           ;; TODO rothko#196 part seq matcher
           [m/?any {:type edit-or-space-edit}]
           [{:type edit-or-space-edit}]
           ;; TODO rothko#196 part seq matcher
           [m/?any {:edited? true}]
           [{:edited? true}]
           n/script-start-matcher))
         [{:type :space}]
         (m/not-matcher
          (m/one-of
           ;; TODO rothko#196 part seq matcher
           [{:type edit-or-space-edit} m/?any]
           [{:type edit-or-space-edit}]
           ;; TODO rothko#196 part seq matcher
           [{:edited? true} m/?any]
           [{:edited? true}]
           n/script-end-matcher))]))
      (sp/multi-path
       (n/expand-selection
        {:shift-start-index -1
         :new-start-offset :last
         :shift-end-index 1
         :new-end-offset 0})
       (edit-paths/create-from-selection "" opts))

      (n/and-paths
       (constantly mark-spaces-as-edited?)
       (n/context? [m/?any [{:type :space}] m/?any]))
      (space-transform/space-split)

      (n/or-paths
       ;; Are we in a space token the start or end of the script, or...
       (n/context? [m/?any [{:type removable-space}] n/script-end-matcher])
       (n/context? [n/script-start-matcher [{:type removable-space}] m/?any])
       ;; ... are we deleting some of the space token, or...
       (n/and-paths
        (n/or-paths
         (n/char-context? [m/?any #" +" #"  ?"])
         (n/char-context? [#"  ?" #" +" m/?any]))
        (n/context? [m/?any [{:type removable-space}] m/?any]))
       ;; ... are we deleting a combination of space and edit tokens, or...
       (n/context?
        [;; TODO rothko#196 part seq matcher
         (m/and-matcher
          (m/not-matcher [m/?any {:type :word}])
          (m/not-matcher [{:type :word}]))
         (m/every (m/one-of {:type :edit} {:type removable-space}))
         ;; TODO rothko#196 part seq matcher
         (m/and-matcher
          (m/not-matcher [{:type :word} m/?any])
          (m/not-matcher [{:type :word}]))])
       ;; ... are we deleting a combination of space-edit and edit tokens
       (n/context? [m/?any (m/every (m/one-of {:type :space-edit} {:type :edit})) m/?any]))
      (sp/multi-path
       n/remove-selection
       (n/merge-similar))

      (n/and-paths
       (n/context?
        [m/?any
         (m/every (m/one-of {:type :word} {:type :space}))
         m/?any])
       n/edited?)
      word-transform/remove-edited?-marking

      (n/and-paths
       (n/context?
        [m/?any
         (m/and-matcher
          (m/every (m/one-of {:type :word} {:type :space} {:type :tag :system? true}))
          (m/not-matcher [{:type :space}]))
         m/?any])
       n/not-edited?)
      (edit-paths/create-from-selection "" opts)

      (n/context? [m/?any [{:type :tag}] m/?any])
      (sp/multi-path
       tag-transform/remove-at-selection
       (n/merge-similar)))]))

(defn transform
  [opts]
  [(sp/collect-one :selection)
   n/token-unbias
   (util/cond-path

    (n/or-paths n/start-of-script? n/empty-document?)
    [sp/DISPENSE
     actions/no-op]

    n/CARET?
    (sp/multi-path (transform-caret (assoc opts :move :left)) n/token-rebias)

    (constantly :else)
    (sp/multi-path (transform-selection opts) n/token-rebias))])
